/**
 * SOCKET CONTEXT
 *
 * Manages Socket.IO connection for real-time updates
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import io from "socket.io-client";
import { useAuth } from "./AuthContext";
import toast from "react-hot-toast";
import { API_CONFIG } from "../config/api";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [botStatus, setBotStatus] = useState("stopped");
  const [botLogs, setBotLogs] = useState([]);
  const [activities, setActivities] = useState([]);
  const { user, isAuthenticated } = useAuth();

  // Function to manually refresh bot status from API
  const refreshBotStatus = useCallback(async () => {
    const token = Cookies.get("auth_token");

    if (!token) {
      console.log("❌ Cannot refresh bot status - no token");
      return;
    }

    try {
      // Decode JWT token to get userId
      const decoded = jwtDecode(token);
      console.log("🔍 Decoded JWT token:", decoded);

      // Use userIDs from JWT (this is the custom userId for database)
      const userIdForBot = decoded.userIDs || decoded.userId;

      if (!userIdForBot) {
        console.log("❌ Cannot refresh bot status - no user ID in token");
        console.log("Available token fields:", Object.keys(decoded));
        return;
      }

      console.log("🔄 Refreshing bot status for user:", userIdForBot);
      console.log("🔄 Token available:", !!token);

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/bot/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("🔄 API response status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("🔄 API response data:", data);

        if (data.success && data.data) {
          const apiStatus = data.data.status; // "running" or "stopped"
          console.log("🔄 Setting bot status to:", apiStatus);
          setBotStatus(apiStatus);

          // Also emit to socket for other connected clients
          if (socket) {
            socket.emit("sync-bot-status", {
              userId: userIdForBot, // Use the same ID we used for API call
              status: apiStatus,
            });
          }
        }
      } else {
        const errorData = await response.text();
        console.error("❌ API response error:", response.status, errorData);
      }
    } catch (error) {
      console.error("❌ Error refreshing bot status:", error);
    }
  }, [socket]); // Remove user dependency since we get it from token

  // Sync initial bot status on mount
  useEffect(() => {
    const token = Cookies.get("auth_token");
    if (isAuthenticated && token) {
      console.log("🔄 Initial bot status sync on mount");
      refreshBotStatus();
    }
  }, [isAuthenticated, refreshBotStatus]);

  useEffect(() => {
    if (isAuthenticated && user) {
      initializeSocket();
    } else {
      disconnectSocket();
    }

    return () => {
      disconnectSocket();
    };
  }, [isAuthenticated, user]);

  const initializeSocket = () => {
    try {
      const serverUrl = API_CONFIG.WS_URL;

      const newSocket = io(serverUrl, {
        transports: ["websocket", "polling"],
        timeout: 10000,
        retries: 5,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        forceNew: true,
      });

      newSocket.on("connect", () => {
        console.log("✅ Socket connected:", newSocket.id);
        setIsConnected(true);
        toast.success("Connected to server", { duration: 2000 });

        // Join user-specific room
        newSocket.emit("join-dashboard", {
          userId: user.userId,
          licenseKey: user.licenseKey,
        });
      });

      newSocket.on("connection-confirmed", (data) => {
        console.log("✅ Connection confirmed:", data);
      });

      newSocket.on("dashboard-joined", async (data) => {
        console.log("✅ Dashboard joined:", data);
        toast.success("Real-time updates active", { duration: 2000 });

        // Fetch and sync initial bot status from database
        try {
          const response = await fetch(
            `${API_CONFIG.BASE_URL}/api/bot/status`,
            {
              headers: {
                Authorization: `Bearer ${Cookies.get("auth_token")}`,
                "Content-Type": "application/json",
              },
            },
          );

          if (response.ok) {
            const statusData = await response.json();
            console.log("📊 Initial bot status from DB:", statusData.data);

            // Update bot status based on database data
            if (statusData.data && statusData.data.isRunning) {
              setBotStatus("running");
              console.log("🤖 Bot status synced: running");
            } else {
              setBotStatus("stopped");
              console.log("🤖 Bot status synced: stopped");
            }
          }
        } catch (error) {
          console.error("❌ Failed to fetch initial bot status:", error);
        }
      });

      newSocket.on("disconnect", (reason) => {
        console.log("❌ Socket disconnected:", reason);
        setIsConnected(false);
        toast.error("Disconnected from server", { duration: 3000 });
      });

      newSocket.on("reconnect", (attemptNumber) => {
        console.log("✅ Socket reconnected after", attemptNumber, "attempts");
        setIsConnected(true);
        toast.success("Reconnected to server", { duration: 2000 });
      });

      newSocket.on("reconnect_attempt", (attemptNumber) => {
        console.log("🔄 Reconnection attempt:", attemptNumber);
      });

      newSocket.on("reconnect_error", (error) => {
        console.error("❌ Reconnection error:", error);
      });

      newSocket.on("connect_error", (error) => {
        console.error("❌ Socket connection error:", error);
        setIsConnected(false);
        toast.error("Connection error. Real-time updates may not work.");
      });

      newSocket.on("pong", (data) => {
        console.log("🏓 Pong received:", data);
      });

      // Bot status updates
      newSocket.on("bot-status", (data) => {
        console.log("🤖 Bot status update:", data);

        // Handle both direct status and database format
        let status = "stopped";
        if (typeof data === "string") {
          status = data;
        } else if (data.status) {
          status = data.status;
        } else if (data.isRunning) {
          status = "running";
        }

        setBotStatus(status);
        console.log("🤖 Bot status set to:", status);

        // Show status notifications
        if (status === "running") {
          toast.success("Bot started successfully");
        } else if (status === "completed") {
          toast.success("Bot session completed");
        } else if (status === "error") {
          toast.error("Bot encountered an error");
        } else if (status === "stopped") {
          toast("Bot stopped");
        }
      });

      // Real-time bot status updates
      newSocket.on("bot-status-update", (data) => {
        console.log("📡 Real-time bot status update:", data);

        // Update bot status based on isActive flag
        if (data.isActive) {
          setBotStatus("running");
          toast.success("Bot started successfully");
        } else {
          setBotStatus("stopped");
          toast("Bot stopped");
        }
      });

      // Real-time bot logs
      newSocket.on("bot-log", (logData) => {
        setBotLogs((prevLogs) => {
          const newLogs = [...prevLogs, logData];
          // Keep only latest 100 logs
          return newLogs.slice(-100);
        });
      });

      // Activity updates
      newSocket.on("activity-update", (activity) => {
        setActivities((prevActivities) => {
          const newActivities = [activity, ...prevActivities];
          // Keep only latest 50 activities
          return newActivities.slice(0, 50);
        });

        // Show activity notifications
        if (activity.success) {
          if (activity.actionType === "connection_sent") {
            toast.success(`Connection sent to ${activity.targetProfile}`);
          } else if (activity.actionType === "direct_message_sent") {
            toast.success(`Message sent to ${activity.targetProfile}`);
          } else if (activity.actionType === "search_performed") {
            toast.success(`Search performed: ${activity.searchKeyword}`);
          }
        }
      });

      // License error handling
      newSocket.on("license-error", (data) => {
        console.error("❌ License error from socket:", data);
        console.log("Current user license status:", user);

        // Temporarily disabled license error notifications
        const SHOW_LICENSE_ERROR_NOTIFICATIONS = false;

        if (SHOW_LICENSE_ERROR_NOTIFICATIONS) {
          // Only show notification if license is actually invalid
          if (user && (!user.isValid || user.isExpired)) {
            toast.error(data.message || "License verification failed");
          } else {
            console.log(
              "🔍 License error received but user license is valid - ignoring notification",
            );
          }
        } else {
          console.log("🔇 License error notifications are disabled");
        }
      });

      // General error handling
      newSocket.on("error", (data) => {
        console.error("❌ Socket error:", data);
        toast.error(data.message || "An error occurred");
      });

      setSocket(newSocket);
    } catch (error) {
      console.error("❌ Socket initialization error:", error);
      toast.error("Failed to initialize real-time connection");
    }
  };

  const disconnectSocket = () => {
    if (socket) {
      console.log("🔌 Disconnecting socket...");
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
      setBotStatus("stopped");
      setBotLogs([]);
      setActivities([]);
    }
  };

  const clearLogs = () => {
    setBotLogs([]);
  };

  const clearActivities = () => {
    setActivities([]);
  };

  const pingServer = () => {
    if (socket && isConnected) {
      socket.emit("ping");
      return true;
    }
    return false;
  };

  const value = {
    socket,
    isConnected,
    botStatus,
    botLogs,
    activities,
    clearLogs,
    clearActivities,
    pingServer,
    refreshBotStatus, // Add this function for manual refresh
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};

export default SocketProvider;
